import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * Sticky cart behavior:
 * - If the main product Add to Cart button exists and is visible:
 *   show sticky Add to Cart.
 * - If the main product Add to Cart button does not exist or is hidden:
 *   hide sticky Add to Cart and quantity controls.
 * - The sticky price remains visible.
 * - No quantity number such as (1) is displayed.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {MutationObserver | null} */
  #productFormMutationObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;

    const target = this.closest('.shopify-section');

    target?.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect,
      { signal }
    );

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartAddComplete,
      { signal }
    );

    document.addEventListener(
      StandardEvents.cartError,
      this.#handleCartAddComplete,
      { signal }
    );

    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#handleQuantityUpdate,
      { signal }
    );

    this.#getInitialQuantity();

    /*
     * Watch the actual product form.
     *
     * Shopify can dynamically add/remove/change the Add to Cart
     * button when variants, inventory, selling plans, etc. change.
     */
    this.#setupProductFormMutationObserver();

    /*
     * Chat visibility handling.
     */
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;

      if (this.#isStuck && this.#isChatActive()) {
        this.#hideStickyBar();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#productFormMutationObserver?.disconnect();

    this.#abortController.abort();

    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }

    if (this.#resetTimeout) {
      clearTimeout(this.#resetTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility.
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();

    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');

    if (!buyButtonsBlock) return;

    /*
     * In themes migrated from 2.0, the footer element doesn't exist.
     */
    const footer =
      document.querySelector('footer') ??
      document.querySelector('[class*="footer-group"]');

    if (!footer) return;

    /*
     * Observer for buy buttons visibility.
     */
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;

      if (!entry) return;

      /*
       * Only show sticky bar if buy buttons have been scrolled past.
       */
      if (!entry.isIntersecting && !this.#isStuck) {
        const rect = entry.target.getBoundingClientRect();

        if (rect.bottom < 0 || rect.top < 0) {
          if (this.#isChatActive()) return;

          /*
           * Synchronize sticky Add to Cart state before showing.
           */
          this.#syncStickyCartVisibility();

          this.#showStickyBar();
        }

        /*
         * If rect.top >= 0, element is below viewport.
         * Don't show sticky bar yet.
         */
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    /*
     * Observer for footer visibility.
     * Hides sticky bar at page bottom.
     */
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;

        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          const rect = buyButtonsBlock.getBoundingClientRect();

          /*
           * Only show if buy buttons are above viewport.
           */
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;

            if (!this.#isChatActive()) {
              this.#syncStickyCartVisibility();
              this.#showStickyBar();
            }
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);

    /*
     * Cache the real product-page Add to Cart button.
     */
    this.#targetAddToCartButton =
      productForm.querySelector('[ref="addToCartButton"]');

    /*
     * Immediately synchronize sticky controls.
     */
    this.#syncStickyCartVisibility();
  }

  /**
   * Watches the actual product form for Shopify DOM changes.
   *
   * This is important because Shopify can change the Add to Cart
   * button dynamically after variant selection.
   */
  #setupProductFormMutationObserver() {
    const productForm = this.#getProductForm();

    if (!productForm) return;

    this.#productFormMutationObserver = new MutationObserver(() => {
      this.#refreshTargetAddToCartButton();
      this.#syncStickyCartVisibility();
    });

    this.#productFormMutationObserver.observe(productForm, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [
        'disabled',
        'hidden',
        'class',
        'style',
        'aria-hidden',
      ],
    });

    this.#refreshTargetAddToCartButton();
    this.#syncStickyCartVisibility();
  }

  /**
   * Refreshes the cached main Add to Cart button.
   */
  #refreshTargetAddToCartButton() {
    const productForm = this.#getProductForm();

    if (!productForm) {
      this.#targetAddToCartButton = null;
      return;
    }

    this.#targetAddToCartButton =
      productForm.querySelector('[ref="addToCartButton"]');
  }

  /**
   * Checks whether the main product-page Add to Cart button
   * is actually visible.
   *
   * A disabled button is still considered present.
   *
   * If the button is removed or hidden, sticky Add to Cart
   * will also be hidden.
   *
   * @returns {boolean}
   */
  #isMainAddToCartVisible() {
    const button = this.#targetAddToCartButton;

    if (!button) return false;

    /*
     * HTML hidden attribute.
     */
    if (button.hidden) return false;

    /*
     * aria-hidden.
     */
    if (button.getAttribute('aria-hidden') === 'true') {
      return false;
    }

    /*
     * Computed CSS visibility.
     */
    const styles = window.getComputedStyle(button);

    if (styles.display === 'none') {
      return false;
    }

    if (styles.visibility === 'hidden') {
      return false;
    }

    if (styles.opacity === '0') {
      return false;
    }

    /*
     * Make sure it actually occupies space.
     */
    const rect = button.getBoundingClientRect();

    if (rect.width === 0 || rect.height === 0) {
      return false;
    }

    return true;
  }

  /**
   * Synchronizes sticky Add to Cart controls with
   * the actual product-page Add to Cart button.
   *
   * The sticky price is NOT changed.
   */
  #syncStickyCartVisibility() {
    const stickyButton = this.refs.addToCartButton;
    const quantityDisplay = this.refs.quantityDisplay;

    if (!stickyButton) return;

    const mainAddToCartVisible =
      this.#isMainAddToCartVisible();

    if (mainAddToCartVisible) {
      /*
       * Main product page has Add to Cart.
       * Show sticky Add to Cart.
       */
      stickyButton.style.display = '';
      stickyButton.removeAttribute('aria-hidden');

      /*
       * NEVER show quantity number such as (1).
       */
      if (quantityDisplay) {
        quantityDisplay.style.display = 'none';
      }

      /*
       * Keep sticky button state synchronized.
       */
      stickyButton.disabled = Boolean(
        this.#targetAddToCartButton?.disabled
      );
    } else {
      /*
       * Main product page DOES NOT have Add to Cart.
       *
       * Hide only sticky Add to Cart and quantity.
       *
       * Sticky price remains visible.
       */
      stickyButton.style.display = 'none';
      stickyButton.setAttribute('aria-hidden', 'true');

      if (quantityDisplay) {
        quantityDisplay.style.display = 'none';
      }
    }
  }

  /**
   * Handles the Add to Cart button click in the sticky bar.
   */
  handleAddToCartClick = async () => {
    /*
     * Always refresh the real product Add to Cart button
     * before allowing sticky Add to Cart.
     */
    this.#refreshTargetAddToCartButton();

    /*
     * If the main product Add to Cart doesn't exist
     * or isn't visible, do nothing.
     */
    if (!this.#isMainAddToCartVisible()) {
      this.#syncStickyCartVisibility();
      return;
    }

    if (!this.#targetAddToCartButton) return;

    /*
     * If the actual product button is disabled,
     * don't trigger it.
     */
    if (this.#targetAddToCartButton.disabled) {
      return;
    }

    this.#targetAddToCartButton.dataset.puppet = 'true';

    this.#targetAddToCartButton.click();

    const cartIcon =
      document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (
      !cartIcon ||
      !this.refs.addToCartButton ||
      !this.refs.productImage
    ) {
      return;
    }

    if (this.#resetTimeout) {
      clearTimeout(this.#resetTimeout);
    }

    const flyToCartElement =
      /** @type {FlyToCart} */
      (document.createElement('fly-to-cart'));

    flyToCartElement.classList.add('fly-to-cart--sticky');

    flyToCartElement.style.setProperty(
      'background-image',
      `url(${this.refs.productImage.src})`
    );

    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([
      this.refs.addToCartButton,
      flyToCartElement,
    ]);

    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles product select events (variant selected and updated).
   */
  #handleProductSelect = (event) => {
    if (
      !(event.target instanceof Element) ||
      event.target.closest('product-card')
    ) {
      return;
    }

    /*
     * Update variant ID from the event.
     */
    const { optionValueId } = event.detail ?? {};

    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    /*
     * Wait for the variant update promise.
     */
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const {
          html,
          productId,
          resource: variant,
        } = detail;

        if (
          productId &&
          productId !== this.dataset.productId
        ) {
          return;
        }

        /*
         * Get the new sticky Add to Cart HTML.
         */
        const newStickyAddToCart =
          /** @type {HTMLElement | null} */
          (html.querySelector('sticky-add-to-cart'));

        if (!newStickyAddToCart) return;

        const newStickyBar =
          newStickyAddToCart.querySelector('[ref="stickyBar"]');

        if (!newStickyBar) return;

        /*
         * Store current visibility state before morphing.
         */
        const currentStuck =
          this.refs.stickyBar.getAttribute('data-stuck') ||
          'false';

        const variantAvailable =
          newStickyAddToCart.dataset.variantAvailable;

        /*
         * Morph the sticky bar content.
         */
        morph(
          this.refs.stickyBar,
          newStickyBar,
          {
            childrenOnly: true,
          }
        );

        /*
         * Restore visibility state.
         */
        this.refs.stickyBar.setAttribute(
          'data-stuck',
          currentStuck
        );

        this.dataset.variantAvailable =
          variantAvailable;

        /*
         * Update variant ID.
         */
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        /*
         * Re-cache the actual product Add to Cart button.
         */
        const productForm = this.#getProductForm();

        if (productForm) {
          this.#targetAddToCartButton =
            productForm.querySelector(
              '[ref="addToCartButton"]'
            );
        }

        /*
         * Variant unavailable.
         */
        if (variant == null) {
          this.#handleVariantUnavailable();
        }

        /*
         * Update button state.
         */
        this.#updateButtonText();

        /*
         * Re-check whether the main product page
         * has an Add to Cart button.
         */
        this.#syncStickyCartVisibility();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[sticky-add-to-cart] Event promise rejected:',
            error
          );
        }
      });
  };

  /**
   * Updates the variant title when variant is unavailable.
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';

    const variantTitleElement =
      this.querySelector('.sticky-add-to-cart__variant');

    const productId = this.dataset.productId;

    const variantPicker =
      document.querySelector(
        `variant-picker[data-product-id="${productId}"]`
      );

    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(
      variantPicker.querySelectorAll('input:checked')
    )
      .map(
        (option) =>
          /** @type {HTMLInputElement} */
          (option).value
      )
      .filter((value) => value !== '')
      .join(' / ');

    if (!selectedOptions) return;

    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete.
   */
  #handleCartAddComplete = (event) => {
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    if (
      'promise' in event &&
      event.promise instanceof Promise
    ) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events.
   *
   * The quantity number is NOT displayed in the sticky button.
   */
  #handleQuantityUpdate = (event) => {
    /*
     * Only respond to product page quantity selector updates,
     * not cart drawer.
     */
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;

    this.#updateButtonText();

    /*
     * Re-check sticky cart visibility.
     */
    this.#syncStickyCartVisibility();
  };

  /**
   * Shows the sticky bar with animation.
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;

    /*
     * Synchronize before showing.
     */
    this.#syncStickyCartVisibility();

    this.#isStuck = true;

    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation.
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;

    this.#isStuck = false;

    stickyBar.dataset.stuck = 'false';
  }

  /**
   * Checks whether Shopify Chat is active on the page.
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) {
      return false;
    }

    return Boolean(
      document.querySelector('shopify-chat')
    );
  }

  /**
   * Gets the product form element.
   */
  #getProductForm() {
    const productId = this.dataset.productId;

    if (!productId) return null;

    const sectionElement =
      this.closest('.shopify-section');

    if (!sectionElement) return null;

    const sectionId =
      sectionElement.id.replace(
        'shopify-section-',
        ''
      );

    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute.
   */
  #getInitialQuantity() {
    this.#currentQuantity =
      parseInt(
        this.dataset.initialQuantity || '1'
      ) || 1;

    this.#updateButtonText();
  }

  /**
   * Updates sticky Add to Cart state.
   *
   * IMPORTANT:
   * The quantity number is completely hidden.
   * The button will only display:
   *
   * Add to cart
   */
  #updateButtonText() {
    const {
      addToCartButton,
      quantityDisplay,
    } = this.refs;

    /*
     * NEVER show the quantity number.
     *
     * This prevents:
     * Add to cart (1)
     * Add to cart (2)
     * etc.
     */
    quantityDisplay.style.display = 'none';

    /*
     * If the main product Add to Cart doesn't exist
     * or is hidden, keep sticky cart hidden.
     */
    if (!this.#isMainAddToCartVisible()) {
      addToCartButton.style.display = 'none';
      addToCartButton.disabled = true;
      return;
    }

    /*
     * Main Add to Cart exists.
     * Show sticky Add to Cart.
     */
    addToCartButton.style.display = '';

    /*
     * Match the disabled state of the actual
     * product-page Add to Cart button.
     */
    addToCartButton.disabled = Boolean(
      this.#targetAddToCartButton?.disabled
    );

    /*
     * Do NOT modify the button's textContent here.
     *
     * This preserves the existing cart icon and
     * Shopify's original "Add to cart" text.
     */
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    StickyAddToCartComponent
  );
}